/** Lado maior aceito no S3. Acima disso o Amplify não otimiza a origem (> 11 MB). */
export const PRODUCT_IMAGE_MAX_EDGE = 1600;
export const PRODUCT_IMAGE_JPEG_QUALITY = 0.82;
/** Teto do arquivo original antes de decodificar no browser. */
export const PRODUCT_IMAGE_MAX_INPUT_MB = 40;
const OUTPUT_TARGET_BYTES = 1_500_000;

export function fitInside(width, height, maxEdge = PRODUCT_IMAGE_MAX_EDGE) {
  const w = Math.max(0, Math.round(Number(width) || 0));
  const h = Math.max(0, Math.round(Number(height) || 0));
  const max = Math.max(w, h);
  if (!max || max <= maxEdge) return { width: w, height: h };
  const scale = maxEdge / max;
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

export function productImageJpegName(name) {
  const base = String(name || "foto").replace(/\.[^.]+$/i, "").trim() || "foto";
  return `${base}.jpg`;
}

function canvasToJpeg(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob"))),
      "image/jpeg",
      quality
    );
  });
}

async function loadImage(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("decode"));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Reduz a foto para no máximo 1600 px no lado maior e grava JPEG sobre fundo branco.
 * JPEG já dentro do limite volta sem reencode.
 */
export async function prepareProductImage(file) {
  const bitmap = await loadImage(file);
  try {
    const srcW = bitmap.width || 0;
    const srcH = bitmap.height || 0;
    const jpeg =
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      /\.jpe?g$/i.test(file.name || "");
    const alreadyFit =
      jpeg &&
      Math.max(srcW, srcH) <= PRODUCT_IMAGE_MAX_EDGE &&
      file.size > 0 &&
      file.size <= OUTPUT_TARGET_BYTES;
    if (alreadyFit) return file;

    const { width, height } = fitInside(srcW, srcH);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    let quality = PRODUCT_IMAGE_JPEG_QUALITY;
    let blob = await canvasToJpeg(canvas, quality);
    while (blob.size > OUTPUT_TARGET_BYTES && quality > 0.5) {
      quality = Math.round((quality - 0.1) * 10) / 10;
      blob = await canvasToJpeg(canvas, quality);
    }

    return new File([blob], productImageJpegName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    if (typeof bitmap.close === "function") bitmap.close();
  }
}
